import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpDownloadProgressEvent,
  HttpEvent,
  HttpEventType,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { SseMessage, SseRequest } from './sse.models';
import { HttpContextToken } from '@angular/common/http';

/**
 * Set to true on every request made by SseClient.
 *
 * Interceptors that retry, time out, cache or unwrap responses, or show a global spinner,
 * should pass these requests through untouched:
 *
 *   if (req.context.get(SSE_STREAM)) {
 *     return next(req);
 *   }
 */
export const SSE_STREAM = new HttpContextToken<boolean>(() => false);

function isDownloadProgress(event: HttpEvent<string>): event is HttpDownloadProgressEvent {
  return event.type === HttpEventType.DownloadProgress;
}

@Injectable({ providedIn: 'root' })
export class SseClient {
  private readonly http = inject(HttpClient);

  public connect(request: SseRequest): Observable<SseMessage> {
    return new Observable<SseMessage>((subscriber) => {
      let processedLength = 0;

      const parser = createParser({
        onEvent: (msg: EventSourceMessage) => {
          subscriber.next({ event: msg.event ?? 'message', data: msg.data, id: msg.id });
        },
      });

      const feedNewText = (fullText: string): void => {
        if (fullText.length > processedLength) {
          parser.feed(fullText.slice(processedLength));
          processedLength = fullText.length;
        }
      };

      const httpSub = this.http
        .request(request.method ?? 'GET', request.url, {
          body: request.body,
          headers: request.headers,
          params: request.params ? new HttpParams({ fromObject: request.params }) : undefined,
          withCredentials: request.withCredentials,
          context: new HttpContext().set(SSE_STREAM, true),
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.Sent) {
              // A new attempt started (e.g. an auth interceptor re-sent it after a token refresh)
              processedLength = 0;
              parser.reset();
            } else if (isDownloadProgress(event)) {
              feedNewText(event.partialText ?? '');
            } else if (event.type === HttpEventType.Response) {
              feedNewText(event.body ?? ''); // flush whatever arrived after the last progress event
            }
          },
          error: (err: unknown) => subscriber.error(err),
          complete: () => subscriber.complete(),
        });

      return (): void => httpSub.unsubscribe(); // aborts the HTTP request
    });
  }
}
